/* Maintenance · papers and services coming up. Design: ConsoleMaintenance.dc.html.
   Built from each bus's papers (insurance and inspection dates, and the service
   due point the maintenance engine works out) plus the engine's own service
   state. Dates, the service schedule, a logged service and "not legal to run"
   acknowledged all open the MaintModal here (2026-10-28), which sends them to
   the classic page's routes with its rules. */
import React, { useMemo, useState } from 'react';
import { useFleet } from '../data/FleetData';
import { maintenanceConfigured } from '../data/maintenance';
import type { Bus, PaperDue } from '../data/model';
import { usePage, useShell } from '../Shell';
import { MaintFocus, MaintModal } from '../MaintModal';
import {
  ChipTone, Empty, IcAlert, IcRetry, PageBody, PageSkeleton, StaleBanner,
  dayMonth, dueIn, n0, plateBeside, plural,
} from '../kit';

type Filter = 'all' | 'papers' | 'services';
type Group = 'overdue' | 'soon' | 'term' | 'later';

interface Item {
  id: string;
  bus: Bus;
  paper: PaperDue;
  group: Group;
  sortKey: number;
  when: string;
  chip: string;
  tone: ChipTone;
  action: string;
  acked: boolean;
}

const SHIELD = <path d="M12 3 4 6v6c0 5 3.5 8 8 9 4.5-1 8-4 8-9V6z" />;
const WRENCH = <path d="M14.7 6.3a4 4 0 0 0-5.4 5.4L3 18l3 3 6.3-6.3a4 4 0 0 0 5.4-5.4l-2.6 2.6-2.4-.6-.6-2.4z" />;

const Maintenance: React.FC = () => {
  const { openAddBus } = useShell();
  const f = useFleet();
  const [filter, setFilter] = useState<Filter>('all');
  const [open, setOpen] = useState<{ mode: 'log' | 'edit'; busId?: string; focus?: MaintFocus } | null>(null);
  const termEnd = f.org.termEnd;

  const items = useMemo<Item[]>(() => {
    const out: Item[] = [];
    const svcState = new Map((f.maintenance || []).map((m) => [m.busId, m.service.state]));
    const acked = new Set((f.maintenance || []).filter((m) => m.complianceAck).map((m) => m.busId));
    const laterGroup = (d: Date | null): Group => (termEnd && d && d.getTime() <= termEnd.getTime() ? 'term' : 'later');
    f.buses.forEach((b) => b.papers.forEach((p, i) => {
      const id = `${b.id}-${p.kind}-${i}`;
      if (p.kind === 'service') {
        const state = svcState.get(b.id);
        const overdue = (p.dueKm != null && p.dueKm <= 0) || state === 'overdue';
        const days = p.due ? dueIn(p.due).days : null;
        const group: Group = overdue ? 'overdue'
          : days != null ? (days < 0 ? 'overdue' : days <= 14 ? 'soon' : laterGroup(p.due))
            : state === 'due_soon' ? 'soon' : laterGroup(null);
        const chip = p.dueKm != null
          ? (p.dueKm <= 0 ? `${n0(-p.dueKm)} km overdue` : `In ${n0(p.dueKm)} km`)
          : p.due ? dueIn(p.due).text : overdue ? 'Overdue' : 'Due soon';
        out.push({
          id, bus: b, paper: p, group,
          sortKey: p.due ? p.due.getTime() : p.dueKm != null ? Date.now() + p.dueKm * 3600000 : Number.MAX_SAFE_INTEGER,
          when: p.due ? `About ${dayMonth(p.due)}` : '—',
          chip, tone: group === 'overdue' ? 'bad' : group === 'soon' ? 'warn' : 'mute', action: 'Log service', acked: false,
        });
        return;
      }
      if (!p.due) return;
      const { text, days } = dueIn(p.due);
      const group: Group = days < 0 ? 'overdue' : days <= 14 ? 'soon' : laterGroup(p.due);
      out.push({
        id, bus: b, paper: p, group, sortKey: p.due.getTime(),
        when: days < 0 ? `Expired ${dayMonth(p.due)}` : dayMonth(p.due),
        chip: text, tone: group === 'overdue' ? 'bad' : group === 'soon' ? 'warn' : 'mute',
        action: 'Update date', acked: days < 0 && acked.has(b.id),
      });
    }));
    return out.sort((a, b) => a.sortKey - b.sortKey);
  }, [f.buses, f.maintenance, termEnd]);

  const shown = items.filter((x) => filter === 'all' || (filter === 'services') === (x.paper.kind === 'service'));
  const groups: { key: Group; label: string }[] = [
    { key: 'overdue', label: 'Overdue' },
    { key: 'soon', label: 'Next 14 days' },
    { key: 'term', label: 'Later this term' },
    { key: 'later', label: termEnd ? 'After this term' : 'Later' },
  ];

  const unset = (f.maintenance || []).filter((m) => m.service.state === 'not_configured' && f.buses.some((b) => b.id === m.busId)).length;
  const maintFailed = f.maintenance === null && !!f.maintError;
  const maintPending = f.maintenance === null && !f.maintError && maintenanceConfigured();
  const firstOverdue = items.find((x) => x.group === 'overdue');

  usePage({
    key: 'ConsoleMaintenance', title: 'Maintenance', zanaContext: `Maintenance · ${plural(items.length, 'item')}`,
    zanaIntro: 'I can plan workshop days and draft booking notes.',
    prompts: [
      firstOverdue ? `What does ${firstOverdue.bus.plate} need before it can run?` : 'What is due before the end of term?',
      'Which services can share one workshop day?',
      'Draft the NTSA booking note',
    ],
  });

  return (
    <PageBody loading={f.loading} skeleton={<PageSkeleton stats rows={7} />}>
      <div className={'wrap' + (f.error ? ' stale' : '')} style={{ maxWidth: '1040px' }}>
        {f.error && <StaleBanner what="buses" since={f.updatedAt} onRetry={f.retry} />}
        <div className="phead">
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <h1 className="h1">Maintenance</h1>
            <p className="sub">
              Papers and services for {plural(f.buses.length, 'bus', 'buses')}. Due dates come from dates and kilometres, whichever is first.
            </p>
          </div>
          {/* Where a bus's insurance and NTSA dates and its service schedule are set
              (Gabriel, 2026-10-28: it used to be a small link under the list). */}
          <div className="row" style={{ gap: 10, flexWrap: 'wrap' }}>
            <button type="button" className="btn b-line" onClick={() => setOpen({ mode: 'edit' })} disabled={f.buses.length === 0}>
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ flex: 'none' }}>
                {SHIELD}
              </svg>
              Set papers and service
            </button>
            <button type="button" className="btn b-ink" onClick={() => setOpen({ mode: 'log' })} disabled={f.buses.length === 0}>
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ flex: 'none' }}>
                <path d="M12 5v14M5 12h14" />
              </svg>
              Log a service
            </button>
          </div>
        </div>

        {maintFailed && items.length > 0 && (
          <div className="banner warn" role="status">
            <span style={{ color: 'var(--warn)' }}><IcAlert /></span>
            <span style={{ flex: 1 }}>
              <b>Couldn’t load service due points.</b> {f.maintError}. Insurance and inspection dates below come from each bus’s record.
            </span>
            <button type="button" className="btn s b-line" onClick={f.refreshMaintenance}><IcRetry size={15} />Try again</button>
          </div>
        )}

        {items.length === 0 ? (
          <section className="panel">
            {maintFailed ? (
              <Empty icon={<IcAlert size={22} />} title="Couldn’t load maintenance" body={`${f.maintError}. Nothing has changed on your buses. Try again in a minute.`}>
                <button type="button" className="btn s b-ink" onClick={f.refreshMaintenance}><IcRetry size={15} />Try again</button>
                <button type="button" className="btn s b-line" onClick={() => setOpen({ mode: 'edit' })}>Add dates</button>
              </Empty>
            ) : f.buses.length === 0 ? (
              <Empty title="No buses yet" body="Add a bus first. Its insurance, inspection and service dates then appear here before they are due.">
                <button type="button" className="btn b-ink" onClick={openAddBus}>Add a bus</button>
              </Empty>
            ) : (
              <Empty title={maintPending ? 'Checking service due points…' : 'No dates on file yet'}
                body="Add each bus’s insurance and inspection expiry and its service interval. They appear here, grouped by when they are due.">
                <button type="button" className="btn b-ink" onClick={() => setOpen({ mode: 'edit' })}>Add dates</button>
              </Empty>
            )}
          </section>
        ) : (
          <section className="panel" style={{ overflow: 'hidden' }}>
            <div className="ph">
              <h2 className="h2">Coming up</h2>
              <div className="seg" role="group" aria-label="Show">
                {(['all', 'papers', 'services'] as Filter[]).map((k) => (
                  <button key={k} type="button" className={filter === k ? 'on' : ''} aria-pressed={filter === k} onClick={() => setFilter(k)}>
                    {k === 'all' ? 'All' : k === 'papers' ? 'Papers' : 'Services'}
                  </button>
                ))}
              </div>
            </div>
            {shown.length === 0 && (
              <Empty title={filter === 'services' ? 'No services on file' : 'No papers on file'}
                body={filter === 'services'
                  ? (maintFailed ? 'Service due points couldn’t be loaded. Try again in a minute.' : 'Set a service interval and the last service reading for each bus to see services here.')
                  : 'Add insurance and NTSA inspection expiry dates for each bus to see them here.'}>
                <button type="button" className="btn s b-ink" onClick={() => setOpen({ mode: 'edit', focus: filter === 'services' ? 'service' : 'insurance' })}>Set papers and service</button>
                <button type="button" className="btn s b-line" onClick={() => setFilter('all')}>Show all</button>
              </Empty>
            )}
            {groups.map((g) => {
              const list = shown.filter((x) => x.group === g.key);
              if (!list.length) return null;
              return (
                <React.Fragment key={g.key}>
                  <div style={{ padding: '14px 20px 6px', borderTop: '1px solid var(--line)' }} className="mono">{g.label} · {list.length}</div>
                  {list.map((x) => (
                    <div key={x.id} className="todo" data-z={`paper:${x.bus.id}`} style={{ borderTop: '0', padding: '10px 20px' }}>
                      <span className="av" style={{ borderRadius: '8px', background: 'var(--s2)' }}>
                        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ flex: 'none' }}>
                          {x.paper.kind === 'service' ? WRENCH : SHIELD}
                        </svg>
                      </span>
                      <div style={{ flex: '1', minWidth: '0', display: 'flex', flexDirection: 'column' }}>
                        <b>{x.paper.label}</b>
                        <span className="sm" style={{ color: 'var(--muted)' }}>
                          {x.bus.name}{plateBeside(x.bus) ? <> · <span className="mono">{x.bus.plate}</span></> : null}{x.acked ? ' · Acknowledged' : ''}
                        </span>
                      </div>
                      <span className="num desk" style={{ width: '140px', color: 'var(--ink2)' }}>{x.when}</span>
                      <span className={`chip c-${x.tone}`} style={{ width: '120px', justifyContent: 'center' }}>{x.chip}</span>
                      <button type="button" className={'btn s ' + (x.group === 'overdue' && !x.acked ? 'b-ink' : 'b-line')}
                        onClick={() => setOpen(x.paper.kind === 'service'
                          ? { mode: 'log', busId: x.bus.id }
                          : { mode: 'edit', busId: x.bus.id, focus: x.paper.kind === 'inspection' ? 'inspection' : 'insurance' })}
                        aria-label={`${x.action}: ${x.paper.label}, ${x.bus.plate}`}>{x.action}</button>
                    </div>
                  ))}
                </React.Fragment>
              );
            })}
            {(unset > 0 || maintPending || (!maintenanceConfigured() && filter !== 'papers')) && (
              <div className="row" style={{ padding: '12px 20px', borderTop: '1px solid var(--line)', gap: 10, flexWrap: 'wrap' }}>
                <span className="sm" style={{ color: 'var(--muted)', flex: 1 }}>
                  {maintPending ? 'Checking service due points…'
                    : !maintenanceConfigured() ? 'Service due points aren’t available here yet. Papers come from each bus’s record.'
                      : `${plural(unset, 'bus has', 'buses have')} no service interval set, so no service is shown for ${unset === 1 ? 'it' : 'them'}.`}
                </span>
                {unset > 0 && !maintPending && (
                  <button type="button" className="btn s b-line" onClick={() => setOpen({
                    mode: 'edit', focus: 'service',
                    busId: (f.maintenance || []).find((m) => m.service.state === 'not_configured' && f.buses.some((b) => b.id === m.busId))?.busId,
                  })}>Set up</button>
                )}
              </div>
            )}
          </section>
        )}
        {open && <MaintModal f={f} mode={open.mode} busId={open.busId} focus={open.focus} onClose={() => setOpen(null)} />}
      </div>
    </PageBody>
  );
};

export default Maintenance;
